import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check session on initial app load
  useEffect(() => {
    const checkCurrentUser = async () => {
      try {
        const response = await authService.getCurrentUser();
        if (response && response.success && response.user) {
          // Ignore admin sessions on the student portal
          if (response.user.role === 'admin') {
            setUser(null);
          } else {
            setUser(response.user);
          }
        } else {
          setUser(null);
        }
      } catch (err) {
        // Not logged in or session expired
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkCurrentUser();
  }, []);

  // Login handler
  const login = async (credentials) => {
    const response = await authService.login(credentials);
    if (response && response.success && response.user) {
      if (response.user.role === 'admin') {
        try {
          await authService.logout();
        } catch (_) {}
        setUser(null);
        throw new Error('Invalid email or password.');
      }
      setUser(response.user);
      return response;
    }
    throw new Error(response.message || 'Login failed');
  };

  // Register handler
  const register = async (userData) => {
    const response = await authService.register(userData);
    if (response && response.success && response.user) {
      setUser(response.user);
      return response;
    }
    throw new Error(response.message || 'Registration failed');
  };

  // Logout handler
  const logout = async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.warn('Logout network error:', err);
    } finally {
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: Boolean(user && user.role !== 'admin'),
    isAdmin: false,
    login,
    register,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
